import { cn } from "@/components/podkit/lib/cn";
import { isEditableElement } from "@/utils/dom";
import { IconArrowDown } from "central-icons/IconArrowDown";
import { IconArrowUp } from "central-icons/IconArrowUp";
import { IconArrowBottomTop } from "central-icons/IconArrowBottomTop";
import type React from "react";
import { createContext, useContext, useRef, type FC, type ReactNode } from "react";
import {
    mergeProps,
    useCheckbox,
    useFocusRing,
    useTable,
    useTableCell,
    useTableColumnHeader,
    useTableHeaderRow,
    useTableRow,
    useTableRowGroup,
    useTableSelectAllCheckbox,
    useTableSelectionCheckbox,
    VisuallyHidden,
    type AriaCheckboxProps,
} from "react-aria";
import {
    Cell,
    Column,
    Row,
    TableBody,
    TableHeader,
    useTableState,
    useToggleState,
    type TableStateProps,
} from "react-stately";
import type { GridNode } from "@react-types/grid";
import type { TableState } from "react-stately";
import type { Key, Node } from "@react-types/shared";

// ============================================================================
// Context
// ============================================================================

export type DataTableDensity = "default" | "condensed";

/** Handle for the select-all checkbox, used by ActionBarContext. */
export type SelectAllHandle = {
    /** DOM id of the select-all checkbox (for `<label htmlFor>`). */
    id: string;
    /** Toggle selection through react-aria's state (native label click alone won't work). */
    toggle: () => void;
};

type DataTableContextValue = {
    density: DataTableDensity;
    stickyHeader: boolean;
    fullBleed: boolean;
    /** Whether ≥1 row is currently selected (implied editing mode). */
    hasSelection: boolean;
    columnClassNames?: Record<string, string>;
};

const DataTableContext = createContext<DataTableContextValue>({
    density: "default",
    stickyHeader: false,
    fullBleed: false,
    hasSelection: false,
});

const useDataTableContext = () => useContext(DataTableContext);

// ============================================================================
// Root Table
// ============================================================================

type StyledTableProps<T extends object> = TableStateProps<T> & {
    "aria-label": string;
    density?: DataTableDensity;
    stickyHeader?: boolean;
    fullBleed?: boolean;
    /** Whether ≥1 row is currently selected (drives implied editing mode). */
    hasSelection?: boolean;
    className?: string;
    columnClassNames?: Record<string, string>;
    isLoading?: boolean;
    emptyState?: ReactNode;
    /** Handler called when a user performs an action on a row (e.g. Enter key or click). */
    onRowAction?: (key: Key) => void;
};

export function StyledTable<T extends object>({
    density = "default",
    stickyHeader = false,
    fullBleed = false,
    hasSelection = false,
    className,
    columnClassNames,
    isLoading,
    emptyState,
    ...props
}: StyledTableProps<T>) {
    const state = useTableState({
        ...props,
        showSelectionCheckboxes: props.selectionMode === "multiple",
    });

    const ref = useRef<HTMLTableElement | null>(null);
    const { collection } = state;
    const { gridProps } = useTable(props, state, ref);

    const isEmpty = [...collection.body.childNodes].length === 0;

    return (
        <DataTableContext.Provider value={{ density, stickyHeader, fullBleed, hasSelection, columnClassNames }}>
            <div className={cn("relative w-full overflow-x-auto", isLoading && "opacity-50 transition-opacity", className)}>
                <table {...gridProps} ref={ref} className="w-full text-left text-base">
                    <StyledTableRowGroup type="thead">
                        {collection.headerRows.map((headerRow) => (
                            <StyledTableHeaderRow key={headerRow.key} item={headerRow} state={state}>
                                {[...headerRow.childNodes].map((column) =>
                                    column.props?.isSelectionCell ? (
                                        <StyledTableSelectAllCell key={column.key} column={column} state={state} />
                                    ) : (
                                        <StyledTableColumnHeader key={column.key} column={column} state={state} />
                                    ),
                                )}
                            </StyledTableHeaderRow>
                        ))}
                    </StyledTableRowGroup>
                    <StyledTableRowGroup type="tbody">
                        {!isEmpty &&
                            [...collection.body.childNodes].map((row) => (
                                <StyledTableRow key={row.key} item={row} state={state}>
                                    {[...row.childNodes].map((cell) =>
                                        cell.props?.isSelectionCell ? (
                                            <StyledTableCheckboxCell key={cell.key} cell={cell} state={state} />
                                        ) : (
                                            <StyledTableCell key={cell.key} cell={cell} state={state} />
                                        ),
                                    )}
                                </StyledTableRow>
                            ))}
                    </StyledTableRowGroup>
                </table>
                {isEmpty && emptyState && <div className="py-8 text-center">{emptyState}</div>}
            </div>
        </DataTableContext.Provider>
    );
}

// ============================================================================
// Row Group (thead / tbody)
// ============================================================================

type StyledTableRowGroupProps = {
    type: "thead" | "tbody";
    children: ReactNode;
};

const StyledTableRowGroup: FC<StyledTableRowGroupProps> = ({ type: Element, children }) => {
    const { rowGroupProps } = useTableRowGroup();
    return <Element {...rowGroupProps}>{children}</Element>;
};

// ============================================================================
// Header Row
// ============================================================================

type StyledTableHeaderRowProps<T extends object> = {
    item: Node<T>;
    state: TableState<T>;
    children: ReactNode;
};

function StyledTableHeaderRow<T extends object>({ item, state, children }: StyledTableHeaderRowProps<T>) {
    const ref = useRef<HTMLTableRowElement | null>(null);
    const { rowProps } = useTableHeaderRow({ node: item }, state, ref);

    return (
        <tr {...rowProps} ref={ref} className="relative border-b border-border-base">
            {children}
        </tr>
    );
}

// ============================================================================
// Column Header
// ============================================================================

type StyledTableColumnHeaderProps<T extends object> = {
    column: GridNode<T>;
    state: TableState<T>;
};

function StyledTableColumnHeader<T extends object>({ column, state }: StyledTableColumnHeaderProps<T>) {
    const ref = useRef<HTMLTableCellElement | null>(null);
    const { columnHeaderProps } = useTableColumnHeader({ node: column }, state, ref);
    const { isFocusVisible, focusProps } = useFocusRing();
    const { density, stickyHeader, columnClassNames } = useDataTableContext();

    const allowsSorting = (column.props as { allowsSorting?: boolean } | undefined)?.allowsSorting;
    const columnClassName = columnClassNames?.[String(column.key)];
    const sortDirection = state.sortDescriptor?.column === column.key ? state.sortDescriptor.direction : undefined;

    return (
        <th
            ref={ref}
            {...mergeProps(columnHeaderProps, focusProps)}
            className={cn(
                density === "condensed" ? "px-3 py-1" : "px-4 py-3.5",
                "h-11 text-base font-bold text-content-primary outline-none",
                allowsSorting && "group cursor-pointer select-none",
                // Sticky header: -top-6 covers the scroll container's p-6 padding
                // gap. pt adds 1.5rem to compensate so text stays in place.
                stickyHeader &&
                    "sticky -top-6 z-10 bg-surface-primary pt-6 shadow-[0_2px_0_0_var(--color-border-base)]",
                isFocusVisible && "rounded-sm ring-1 ring-inset ring-border-brand",
                columnClassName,
            )}
        >
            <span className="flex items-center gap-1">
                {column.rendered}
                {allowsSorting && (
                    <span
                        className={cn(
                            "inline-flex",
                            !sortDirection && !isFocusVisible && "opacity-0 group-hover:opacity-100",
                        )}
                    >
                        {sortDirection === "ascending" ? (
                            <IconArrowUp size={16} />
                        ) : sortDirection === "descending" ? (
                            <IconArrowDown size={16} />
                        ) : (
                            <IconArrowBottomTop size={16} />
                        )}
                    </span>
                )}
            </span>
        </th>
    );
}

// ============================================================================
// Select All Cell
// ============================================================================

type StyledTableSelectAllCellProps<T extends object> = {
    column: GridNode<T>;
    state: TableState<T>;
};

function StyledTableSelectAllCell<T extends object>({ column, state }: StyledTableSelectAllCellProps<T>) {
    const ref = useRef<HTMLTableCellElement | null>(null);
    const { columnHeaderProps } = useTableColumnHeader({ node: column }, state, ref);
    const { checkboxProps } = useTableSelectAllCheckbox(state);
    const { density, stickyHeader } = useDataTableContext();

    return (
        <th
            ref={ref}
            {...columnHeaderProps}
            className={cn(
                density === "condensed" ? "py-1.5 pl-4 pr-2" : "py-4 pl-4 pr-2",
                "h-11 w-10 overflow-visible",
                stickyHeader &&
                    "sticky -top-6 z-10 bg-surface-primary pt-6 shadow-[0_2px_0_0_var(--color-border-base)] before:absolute before:-left-0.5 before:top-0 before:h-full before:w-0.5 before:bg-surface-primary",
            )}
        >
            {state.selectionManager.selectionMode === "single" ? (
                <VisuallyHidden>{checkboxProps["aria-label"]}</VisuallyHidden>
            ) : (
                <AriaCheckbox {...checkboxProps} />
            )}
        </th>
    );
}

// ============================================================================
// Body Row
// ============================================================================

type StyledTableRowProps<T extends object> = {
    item: Node<T>;
    state: TableState<T>;
    children: ReactNode;
};

/**
 * Strip pointer/mouse/press event handlers from react-aria's rowProps.
 * This prevents row-level clicks from toggling selection while keeping
 * keyboard navigation and focus management intact. Checkbox cells have
 * their own independent event handling and are unaffected.
 */
function stripSelectionPointerEvents<T extends object>(props: T): T {
    const cleaned = { ...props };
    for (const key of Object.keys(cleaned)) {
        if (key.startsWith("onPointer") || key.startsWith("onMouse") || key === "onClick") {
            delete (cleaned as Record<string, unknown>)[key];
        }
    }
    return cleaned;
}

function StyledTableRow<T extends object>({ item, state, children }: StyledTableRowProps<T>) {
    const ref = useRef<HTMLTableRowElement | null>(null);
    const { rowProps, isPressed } = useTableRow({ node: item }, state, ref);
    const { isFocusVisible, focusProps } = useFocusRing();
    const { hasSelection } = useDataTableContext();

    const isSelected = state.selectionManager.isSelected(item.key);
    const isSelectable = state.selectionManager.selectionMode !== "none";

    // When no rows are selected, strip pointer events from the row so clicks
    // don't toggle selection. Checkboxes still work via their own handlers.
    const effectiveRowProps = isSelectable && !hasSelection ? stripSelectionPointerEvents(rowProps) : rowProps;

    // 'x' key toggles selection on the focused row (adapted from Linear/Gmail)
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "x" && isSelectable && !e.metaKey && !e.ctrlKey && !e.altKey) {
            if (isEditableElement(e.target)) return;
            e.preventDefault();
            state.selectionManager.toggleSelection(item.key);
            return;
        }
    };

    return (
        <tr
            ref={ref}
            data-testid={String(item.key)}
            {...mergeProps(effectiveRowProps, focusProps, { onKeyDown: handleKeyDown })}
            className={cn(
                "group border-b border-border-base last:border-b-0",
                "border-l-2",
                "transition-colors duration-75",
                "outline-none",
                isSelected ? "border-l-content-brand bg-surface-brand/5" : "hover:bg-muted/5 border-l-transparent",
                isPressed && "bg-muted/10",
                isFocusVisible && "ring-1 ring-inset ring-border-brand",
                // Implied editing mode: pointer cursor on cells when selecting,
                // auto (browser default — text cursor over text) otherwise.
                isSelectable && hasSelection && "[&>td]:cursor-pointer",
                isSelectable && !hasSelection && "[&>td]:cursor-auto",
            )}
        >
            {children}
        </tr>
    );
}

// ============================================================================
// Body Cell
// ============================================================================

type StyledTableCellProps<T extends object> = {
    cell: GridNode<T>;
    state: TableState<T>;
};

function StyledTableCell<T extends object>({ cell, state }: StyledTableCellProps<T>) {
    const ref = useRef<HTMLTableCellElement | null>(null);
    const { gridCellProps } = useTableCell({ node: cell }, state, ref);
    const { isFocusVisible, focusProps } = useFocusRing();
    const { density, columnClassNames } = useDataTableContext();
    const columnClassName = cell.column?.key ? columnClassNames?.[String(cell.column.key)] : undefined;

    return (
        <td
            ref={ref}
            {...mergeProps(gridCellProps, focusProps)}
            className={cn(
                density === "condensed" ? "px-3 py-1" : "px-4 py-3.5",
                "whitespace-nowrap text-base outline-none",
                isFocusVisible && "rounded-sm ring-1 ring-inset ring-border-brand",
                columnClassName,
            )}
        >
            {cell.rendered}
        </td>
    );
}

// ============================================================================
// Selection Checkbox Cell (per-row)
// ============================================================================

type StyledTableCheckboxCellProps<T extends object> = {
    cell: GridNode<T>;
    state: TableState<T>;
};

function StyledTableCheckboxCell<T extends object>({ cell, state }: StyledTableCheckboxCellProps<T>) {
    const ref = useRef<HTMLTableCellElement | null>(null);
    const { gridCellProps } = useTableCell({ node: cell }, state, ref);
    const { checkboxProps } = useTableSelectionCheckbox({ key: cell.parentKey! }, state);
    const { density } = useDataTableContext();

    return (
        <td
            ref={ref}
            {...gridCellProps}
            className={cn(density === "condensed" ? "py-1.5 pl-4 pr-2" : "py-2.5 pl-4 pr-2", "w-10")}
        >
            <AriaCheckbox {...checkboxProps} />
        </td>
    );
}

// ============================================================================
// Checkbox (uses react-aria useCheckbox for proper selection integration)
// ============================================================================

function AriaCheckbox(props: AriaCheckboxProps & { id?: string }) {
    const ref = useRef<HTMLInputElement | null>(null);
    const state = useToggleState(props);
    const { inputProps } = useCheckbox(props, state, ref);

    return (
        <div className="flex items-center gap-2">
            <input
                {...inputProps}
                id={props.id ?? inputProps.id}
                ref={ref}
                className={cn(
                    // Larger hit target for coarse pointers (touch devices)
                    "size-4 rounded border pointer-coarse:size-5",
                    "cursor-pointer",
                    "accent-content-brand",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-border-brand focus-visible:ring-offset-1",
                )}
            />
        </div>
    );
}

// ============================================================================
// Re-exports for collection building
// ============================================================================

export { Cell, Column, Row, TableBody, TableHeader };
